import { useCallback, useEffect } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { updateUserSettings } from '@/api/apiService'
import { PlantDto } from '@/core/models/types'
import { QueryKeys } from '@/core/models/enum'
import {
    addSnackBarMessage,
    currentPlantAtom,
    currentPlantHydratedAtom,
    resetPlantFiltersAtom,
} from '@/core/store/globalStore'
import { useGetPlants } from '@/core/hooks-and-services/onboardedSensorsService'
import { useGetUserSettings } from '@/core/hooks-and-services/userSettingsService'

export const ALL_PLANTS_OPTION: PlantDto = { shortName: '', description: 'All plants', sapPlantId: null }

export function normalizePlantSelection(next: PlantDto | undefined): PlantDto | null {
    return next && next !== ALL_PLANTS_OPTION ? next : null
}

export function useCurrentPlant() {
    const [currentPlant, setCurrentPlantAtom] = useAtom(currentPlantAtom)
    const [isHydrated, setIsHydrated] = useAtom(currentPlantHydratedAtom)
    const { data: plants = [], isLoading: plantsLoading } = useGetPlants()
    const { data: settings, isLoading: settingsLoading } = useGetUserSettings()
    const queryClient = useQueryClient()
    const resetPlantFilters = useSetAtom(resetPlantFiltersAtom)

    useEffect(() => {
        if (isHydrated) return
        if (plantsLoading || settingsLoading) return
        if (plants.length === 0) return

        const savedShortName = settings?.plant ?? null
        const found = savedShortName ? (plants.find((p) => p.shortName === savedShortName) ?? null) : null
        setCurrentPlantAtom(found)
        setIsHydrated(true)
    }, [isHydrated, plants, plantsLoading, settings, settingsLoading, setCurrentPlantAtom, setIsHydrated])

    const setCurrentPlant = useCallback(
        async (plant: PlantDto | null): Promise<boolean> => {
            if (plant?.shortName !== currentPlant?.shortName) resetPlantFilters()
            setCurrentPlantAtom(plant)
            if (!settings?.userId) return false
            try {
                await updateUserSettings({
                    userId: settings.userId,
                    plant: plant?.shortName ?? null,
                    consentTrendingApp: settings.consentTrendingApp ?? false,
                    isNew: false,
                })
                queryClient.invalidateQueries({ queryKey: [QueryKeys.UserSettings] })
                return true
            } catch (err) {
                console.error('update user settings failed', err)
                // No response means the request never reached the backend, so the service worker has
                // queued it for replay - the selection is not lost and must not be reported as an error.
                if (isAxiosError(err) && !err.response) return true
                addSnackBarMessage('Could not save your plant selection. Please try again.')
                return false
            }
        },
        [settings, queryClient, currentPlant, resetPlantFilters, setCurrentPlantAtom]
    )

    return {
        currentPlant,
        setCurrentPlant,
        plants,
        isLoading: plantsLoading || settingsLoading,
        isHydrated,
    }
}
